<script setup lang="ts">
import LucideIcon from '@/components/common/LucideIcon.vue'

defineProps<{
  /** Titre affiché (encre sur fond crème). */
  title: string
  /** Sous-titre optionnel sous le titre. */
  subtitle?: string
  /** Nom d'icône Lucide ; rend une pastille à gauche. */
  icon?: string
  /** Affiche une croix ronde à droite (désactivée par défaut).
   *  Ignoré si le slot `trailing` est fourni (le slot a la priorité). */
  showClose?: boolean
  /** Libellé aria du bouton de fermeture (défaut : 'Fermer'). */
  closeLabel?: string
  /**
   * Laisse le titre passer à la ligne (2 lignes max) au lieu de le tronquer.
   * Pour les titres qui sont une vraie phrase, ex. une question posée au parent :
   * une question coupée en plein milieu ne se lit pas.
   */
  wrapTitle?: boolean
}>()

defineEmits<{ close: [] }>()
</script>

<template>
  <!-- Header standard : div crème (jamais ion-toolbar, qui a un fond
       translucide plus foncé). En mode sheet, le handle natif d'Ionic
       occupe déjà le haut, donc le padding top reste modéré. -->
  <div class="flex items-center gap-3 px-5 pt-4 pb-2">
    <!-- Slot leading : visuel custom à gauche (ex. avatar enfant, pastille
         colorée). S'il est fourni, il remplace la pastille d'icône Lucide. -->
    <slot name="leading">
      <span
        v-if="icon"
        data-test="header-icon"
        class="w-[34px] h-[34px] rounded-[11px] bg-joy-soft text-brand-deep flex items-center justify-center shrink-0"
      >
        <LucideIcon :name="icon" :size="17" />
      </span>
    </slot>
    <div class="flex-1 min-w-0">
      <h2
        class="text-[17px] font-semibold text-ink"
        :class="wrapTitle ? 'text-balance line-clamp-2 leading-snug' : 'truncate'"
      >{{ title }}</h2>
      <p
        v-if="subtitle"
        data-test="header-subtitle"
        class="text-[13px] text-ink-3 mt-0.5 truncate"
      >{{ subtitle }}</p>
    </div>
    <!-- Slot trailing ; si absent, la croix optionnelle prend sa place. -->
    <slot name="trailing" />
    <!-- 44px hit area (a11y minimum) around a 30px visual circle. -->
    <button
      v-if="showClose && !$slots.trailing"
      data-test="header-close"
      type="button"
      class="-mr-2 w-11 h-11 flex items-center justify-center shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-strong"
      :aria-label="closeLabel ?? 'Fermer'"
      @click="$emit('close')"
    >
      <span class="w-[30px] h-[30px] rounded-full bg-warm-2 text-ink-2 flex items-center justify-center active:bg-warm-sunken transition-colors">
        <LucideIcon name="x" :size="16" />
      </span>
    </button>
  </div>
</template>
